import { WifiOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { useOfflineSync } from '@/hooks/useOfflineSync'
import { Button } from '@/components/ui/button'

export function OfflineIndicator() {
  const { isOnline, isSyncing, queuedCount, syncErrors, triggerSync, clearFailedMutations } = useOfflineSync()

  // Show offline indicator when offline
  if (!isOnline) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-bottom-5">
        <WifiOff className="w-5 h-5" />
        <div>
          <p className="font-medium">Você está offline</p>
          {queuedCount > 0 && (
            <p className="text-xs opacity-90">
              {queuedCount} {queuedCount === 1 ? 'alteração pendente' : 'alterações pendentes'}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Show syncing indicator when syncing
  if (isSyncing) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-bottom-5">
        <RefreshCw className="w-5 h-5 animate-spin" />
        <div>
          <p className="font-medium">Sincronizando...</p>
          {queuedCount > 0 && (
            <p className="text-xs opacity-90">
              {queuedCount} {queuedCount === 1 ? 'item restante' : 'itens restantes'}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Show success indicator briefly after sync
  if (queuedCount === 0 && syncErrors.length === 0) {
    return null
  }

  // Show error indicator if there are sync errors
  if (syncErrors.length > 0) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-in slide-in-from-bottom-5 max-w-md">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Erro ao sincronizar</p>
            <p className="text-xs opacity-90 mt-1">
              {syncErrors.length} {syncErrors.length === 1 ? 'operação falhou' : 'operações falharam'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-red-600 h-7 px-2 text-xs"
              onClick={triggerSync}
            >
              Tentar novamente
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-red-600 h-7 px-2 text-xs"
              onClick={clearFailedMutations}
            >
              Limpar
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Show queued items indicator
  if (queuedCount > 0) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-700 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
        <CheckCircle className="w-5 h-5" />
        <div>
          <p className="text-sm font-medium">
            {queuedCount} {queuedCount === 1 ? 'alteração na fila' : 'alterações na fila'}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-slate-600 h-7 px-2 text-xs"
          onClick={triggerSync}
        >
          Sincronizar agora
        </Button>
      </div>
    )
  }

  return null
}
